import { Fragment, useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckSquareIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import { useCan } from '@/hooks/useCan';
import { runTasksSummary } from './runTasksSummary';
import { runTaskState } from './runTaskState';
import type { RunActionProps } from './workbookIntegrations';

/** How often, and for how long, a just-completed Run's Tasks are re-read. */
const SETTLE_POLL_MS = 1000;
const SETTLE_WINDOW_MS = 10_000;

/**
 * Whether this Run completed while shown, within the last few seconds. Its
 * `completed` status is saved before the Task rules open its Tasks, so they
 * may appear a moment after it reads as completed.
 */
function useJustCompleted(status: string) {
  const previous = useRef(status);
  const [settling, setSettling] = useState(false);
  useEffect(() => {
    const was = previous.current;
    previous.current = status;
    if (status !== 'completed' || was === 'completed') return;
    setSettling(true);
    const timer = window.setTimeout(() => setSettling(false), SETTLE_WINDOW_MS);
    return () => window.clearTimeout(timer);
  }, [status]);
  return settling;
}

/**
 * Tasks' Run action: the Tasks this Run opened, each linked to its page, or,
 * on the newest Run a Task rule reads while it opened none, Open Tasks to
 * apply the rules to it by hand.
 */
export function RunTasks({ editor, run }: RunActionProps) {
  const gt = useGT();
  const [canCreate] = useCan(Action.Create, Subject.Task);
  const queryClient = useQueryClient();
  const settling = useJustCompleted(run.status);
  const tasks = useQuery(
    orpc.workbooks.tasks.queryOptions({
      input: { workbookId: editor.workbookId },
      // Re-read quickly while a just-completed Run may still be opening Tasks.
      refetchInterval: (query) =>
        settling &&
        !query.state.data?.some((task) => task.workbookRunId === run.id)
          ? SETTLE_POLL_MS
          : false,
    }),
  );
  useEffect(() => {
    if (settling)
      void queryClient.invalidateQueries({
        queryKey: orpc.workbooks.tasks.key(),
      });
  }, [settling, queryClient]);
  const open = useMutation(
    orpc.workbooks.openRunTasks.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.workbooks.tasks.key(),
        }),
    }),
  );
  const { opened, canOpen } = runTaskState(
    run,
    editor.runs.data ?? [],
    editor.definition.taskRules,
    tasks.data ?? [],
  );
  const summary = open.data ? runTasksSummary(open.data) : null;
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-xs">
      {opened.length > 0 && (
        <span>
          <span className="text-muted-foreground">
            {gt('{count} tasks opened:', {
              context: 'workbook run: tasks its task rules opened',
              count: String(opened.length),
            })}
          </span>{' '}
          {opened.map((task, index) => (
            <Fragment key={task.id}>
              {index > 0 && ', '}
              <Link
                to="/tasks/$taskId"
                params={{ taskId: task.id }}
                title={task.title}
                className="font-medium text-primary underline underline-offset-2"
              >
                {task.displayId ?? task.title}
              </Link>
            </Fragment>
          ))}
        </span>
      )}
      {canOpen && canCreate && !settling && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          disabled={open.isPending}
          onClick={() =>
            open.mutate({ workbookId: editor.workbookId, runId: run.id })
          }
        >
          <CheckSquareIcon />
          {gt('Open tasks', { context: 'workbook open tasks from a run' })}
        </Button>
      )}
      {summary && (
        <span role="status" className="text-muted-foreground">
          {gt('{opened} opened · {existing} opened before', {
            context: 'workbook tasks opened from a run',
            opened: String(summary.opened),
            existing: String(summary.existing),
          })}
          {summary.truncated &&
            ` · ${gt('first 100 rows only', {
              context:
                'workbook tasks opened only for the first rows of a result',
            })}`}
        </span>
      )}
      {open.error && (
        <span role="alert" className="text-destructive">
          {open.error.message}
        </span>
      )}
    </span>
  );
}
